import { GitBranch, Users } from 'lucide-react';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '../../ui';
import { statusLabel, statusTone } from '../../features/tasks/taskModel';
import type { GoalTraceability } from '../../features/goals/traceability';

export interface TraceabilityPanelProps {
  traceability: GoalTraceability;
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-surface-border bg-surface-1/50 px-3 py-2.5">
      <p className="text-xs text-zinc-500">{label}</p>
      <p className="mt-0.5 text-lg font-semibold tabular-nums text-zinc-100">
        {value}
      </p>
      {sub && <p className="text-[11px] text-zinc-500">{sub}</p>}
    </div>
  );
}

/**
 * Aggregated engineering-work rollup for a goal, computed client-side from the
 * linked tasks + their estimates. Shows story counts, effort (Total /
 * Completed / Remaining), contributing engineers and a delivery forecast —
 * awareness only, never a productivity or ranking metric.
 */
export function TraceabilityPanel({ traceability: t }: TraceabilityPanelProps) {
  const effortUnit = t.effortUnit;
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            <GitBranch className="h-4 w-4 text-accent" />
            Engineering Traceability
          </span>
        </CardTitle>
        <Badge tone="neutral">
          {t.storyCompletionPct}% stories done
        </Badge>
      </CardHeader>
      <CardBody className="space-y-4">
        {/* Story rollup */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Total stories" value={t.totalStories} />
          <Stat label="Completed" value={t.completedStories} />
          <Stat label="Remaining" value={t.remainingStories} />
          <Stat
            label="Blocked"
            value={t.blockedStories}
            sub={t.blockedStories > 0 ? 'needs attention' : undefined}
          />
        </div>

        {/* Effort rollup */}
        {t.hasEffort ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Stat label="Total effort" value={t.totalEffort} sub={effortUnit} />
            <Stat label="Completed effort" value={t.completedEffort} sub={effortUnit} />
            <Stat label="Remaining effort" value={t.remainingEffort} sub={effortUnit} />
          </div>
        ) : (
          <p className="rounded-xl border border-dashed border-surface-border px-3 py-2.5 text-xs text-zinc-500">
            No estimate effort found on the linked stories. Attach estimates to
            linked tasks to roll up Total / Completed / Remaining effort.
          </p>
        )}

        {/* Delivery forecast */}
        <div className="rounded-xl border border-surface-border bg-surface-1/50 px-3 py-2.5">
          <p className="text-xs uppercase tracking-wide text-zinc-500">
            Delivery forecast
          </p>
          <p className="mt-1 text-sm text-zinc-300">{t.deliveryForecast}</p>
        </div>

        {/* Contributing engineers */}
        {t.contributingEngineers.length > 0 && (
          <div>
            <p className="mb-1.5 flex items-center gap-1.5 text-xs uppercase tracking-wide text-zinc-500">
              <Users className="h-3.5 w-3.5" />
              Contributing engineers ({t.contributingEngineers.length})
            </p>
            <div className="flex flex-wrap gap-1.5">
              {t.contributingEngineers.map((eng) => (
                <Badge key={eng} tone="info">
                  {eng}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Linked task list */}
        {t.linkedTasks.length > 0 && (
          <div>
            <p className="mb-1.5 text-xs uppercase tracking-wide text-zinc-500">
              Linked stories
            </p>
            <ul className="divide-y divide-surface-border rounded-xl border border-surface-border">
              {t.linkedTasks.map((task) => (
                <li
                  key={task.id}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <span className="min-w-0 truncate text-sm text-zinc-200">
                    {task.title}
                    {task.assignee && (
                      <span className="text-zinc-500"> · {task.assignee}</span>
                    )}
                  </span>
                  <Badge tone={statusTone(task.status)}>
                    {statusLabel(task.status)}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Unresolved links */}
        {t.unresolvedStoryIds.length > 0 && (
          <p className="text-xs text-amber-400/80">
            {t.unresolvedStoryIds.length} linked story{' '}
            {t.unresolvedStoryIds.length === 1 ? 'id' : 'ids'} could not be
            resolved to a task (it may have been deleted).
          </p>
        )}
      </CardBody>
    </Card>
  );
}

export default TraceabilityPanel;
